import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import * as _ from 'lodash-es';
import {Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {extractListResponse, handleResponseError} from '../../common/api-helpers';
import {AppConfigService} from '../../services/config.service';
import {AutocompleteProvider, AutocompleteProviderConfig, AutocompleteSearchType} from './autocomplete-config';

@Injectable()
export class AutocompleteService {

    private static isAutocompleteProvider<R>(type: AutocompleteSearchType<R>): type is AutocompleteProvider<R> {
        return !!(type as AutocompleteProvider<R>).autocomplete;
    }

    private static isAutocompleteProviderConfig<R>(
        type: AutocompleteSearchType<R>
    ): type is AutocompleteProviderConfig<R> {
        return !!(type as AutocompleteProviderConfig<R>).provider;
    }

    private dedicatedApiUrl: string;

    constructor(
        private httpClient: HttpClient,
        private appConfig: AppConfigService
    ) {
        this.dedicatedApiUrl = appConfig.api.getPath('api/v1/search');
    }

    public simpleSearch<R, T extends AutocompleteSearchType<R> = AutocompleteSearchType<R>>(
        term: string,
        type: T
    ): Observable<R[]> {
        let result: Observable<R[]> = of([]);

        term = _.trim(term);

        if (type) {
            if (_.isString(type)) {
                result = this.searchWithDedicatedApi(type, term);
            } else if (AutocompleteService.isAutocompleteProvider(type)) {
                result = this.searchWithCustomService(type, term);
            } else if (AutocompleteService.isAutocompleteProviderConfig(type)) {
                result = this.searchWithCustomService(type.provider, term, type);
            }
        }

        return result;
    }

    private searchWithDedicatedApi(type: string, term: string): Observable<any> {
        const requestParams = new HttpParams({
            fromString: JSON.stringify({
                query: term,
                type
            })
        });

        return this.httpClient.get(this.dedicatedApiUrl, {params: requestParams}).pipe(
            map(extractListResponse),
            catchError(handleResponseError)
        );
    }

    private searchWithCustomService<R>(
        service: AutocompleteProvider<R>,
        term: string,
        config?: AutocompleteProviderConfig<R>
    ): Observable<R[]> {
        let result: Observable<R[]> = of([]);

        if (service && service.autocomplete) {
            result = service.autocomplete(term, config);
        }

        return result;
    }
}
